/** @format */

import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import {
	FulfillmentButtonInterface,
	selectSameDayDeliveryButtonText,
	selectShipMode,
	setFulfillmentButtonsShipMode
} from '@pdp/add-to-cart/data-access';
import { combineLatest, Observable, Subject, takeUntil } from 'rxjs';
import { selectResolvedSku, selectUserZipCode } from '@pdp/data-access/state';

import { ProductSku } from '@shared/data-access/interfaces';
import { Store } from '@ngrx/store';

@Component({
    selector: 'pdp-feature-same-day-delivery-button',
    templateUrl: './same-day-delivery-button.component.html',
    styleUrls: ['./same-day-delivery-button.component.scss'],
    standalone: false
})
export class SameDayDeliveryButtonComponent implements OnInit, OnDestroy {
	/**
	 * @description state selector returns an object that has the information to display for the fulfillment button and button label
	 */
	sameDayDeliveryButtonText$: Observable<FulfillmentButtonInterface> = this.store.select(
		selectSameDayDeliveryButtonText
	);

	/**
	 * @description selects shipMode from fulfillment slice of state.
	 * This is used to determine the selected CSS state of the fulfillment button and
	 * is passed to the analytics event so they know which method has been chosen.
	 */
	shipMode$ = this.store.select(selectShipMode);

	skuResolved$ = this.store.select(selectResolvedSku);

	userZipCode$ = this.store.select(selectUserZipCode);

	private UNSUBSCRIBE$: Subject<void> = new Subject();
	fulfillmentIconName = 'fulfillment-sameday-outlined';
	fulfillmentButtonTextSDDynamic = '';
	isZipcodeNotSetAndSkuResolved = false;

	constructor(
		private cdr: ChangeDetectorRef,
		private store: Store
	) {}

	ngOnInit() {
		this.subscribeToFulfillmentChanges();
	}

	ngOnDestroy(): void {
		this.UNSUBSCRIBE$.next();
		this.UNSUBSCRIBE$.complete();
	}

	/**
	 * @description when the button is clicked and action is dispatched to update shipMode in the fulfillment slice of state
	 */
	setShipMode() {
		this.store.dispatch(setFulfillmentButtonsShipMode({ shipMode: 'sameDayDelivery' }));
	}

	subscribeToFulfillmentChanges() {
		const observablesToCombine: [
			Observable<FulfillmentButtonInterface>,
			Observable<string>,
			Observable<ProductSku>?,
			Observable<string>?
		] = [this.sameDayDeliveryButtonText$, this.shipMode$];
		observablesToCombine.push(this.skuResolved$, this.userZipCode$);
		combineLatest(observablesToCombine)
			.pipe(takeUntil(this.UNSUBSCRIBE$))
			.subscribe(([sameDayDeliveryButtonText, shipMode, ...otherObservables]) => {
				const buttonEnabled = !sameDayDeliveryButtonText.fulfillmentButtonDisabledClass;
				this.getFulfillmentIconName(shipMode, buttonEnabled);
				const [skuResolved, userZipCode] = otherObservables;
				if ((userZipCode === 'null' || !userZipCode) && skuResolved) {
					this.isZipcodeNotSetAndSkuResolved = true;
					this.getFulfillmentIconName(shipMode, true);
				}
				this.fulfillmentButtonTextSDDynamic = this.getSameDayDeliveryTimeText(
					sameDayDeliveryButtonText.fulfillmentButtonText
				);
				this.cdr.detectChanges();
			});
	}

	getFulfillmentIconName(shipMode: string, buttonEnabled: boolean): void {
		if (shipMode === 'sameDayDelivery' && buttonEnabled) {
			this.fulfillmentIconName = 'fulfillment-sameday-filled';
		} else {
			this.fulfillmentIconName = 'fulfillment-sameday-outlined';
		}
	}

	getSameDayDeliveryTimeText(fulfillmentButtonText: string): string {
		if (this.isZipcodeNotSetAndSkuResolved) {
			return '';
		}

		const lowercaseFulfillmentButtonText = fulfillmentButtonText.toLowerCase();
		const isDeliveryTimeText = lowercaseFulfillmentButtonText.includes('order');

		if (isDeliveryTimeText) {
			const d = new Date();
			const hours = d.getHours();
			const minutes = d.getMinutes();
			const timeInMinutes = hours * 60 + minutes;
			const threshold = 14 * 60; // 2pm in minutes
			if (timeInMinutes < threshold) {
				return 'Order now, get it today by 7pm';
			} else {
				return 'Order now, get it tomorrow by 7pm';
			}
		}
		return fulfillmentButtonText;
	}
}
